import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { api, formatApiError } from '@/App';
import { Plus, X, PaperPlaneTilt } from '@phosphor-icons/react';

const money = (v) => `R ${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const addDays = (days) => new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);
const emptyLine = () => ({ description: '', quantity: 1, unit_price: '' });

const ONE_OFF = '__one_off__';

/** Shared builder for in-app quotations and invoices (line items, VAT-inclusive pricing). */
export const DocumentBuilderDialog = ({ kind, open, onOpenChange, companies = [], cases = [], settings, prefill, onCreated }) => {
  const isQuote = kind === 'quotation';
  const termDays = Number((isQuote ? settings?.quote_validity_days : settings?.payment_terms_days) || 7);
  const defaultVat = Number(((settings?.vat_rate ?? 0.15) * 100).toFixed(2));

  const blank = () => ({
    company_id: '',
    client: { name: '', email: '', phone: '', address: '', vat_number: '' },
    client_vat_number: '',
    case_id: 'none',
    quotation_id: null,
    line_items: [emptyLine()],
    vat_rate: defaultVat,
    issue_date: today(),
    end_date: addDays(termDays),
    notes: '',
    reference: '',
  });

  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ ...blank(), ...(prefill || {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const oneOff = form.company_id === ONE_OFF;

  // Customer list: parents first, each followed by its sub-accounts (which can also be quoted/invoiced)
  const customerOptions = useMemo(() => {
    const active = companies.filter((c) => !c.archived_at);
    const byParent = {};
    active.forEach((c) => {
      const key = c.parent_company_id || '__root__';
      (byParent[key] = byParent[key] || []).push(c);
    });
    const sortName = (a, b) => (a.company_name || '').localeCompare(b.company_name || '');
    const out = [];
    const walk = (company, depth) => {
      out.push({ ...company, depth });
      (byParent[company.company_id] || []).sort(sortName).forEach((child) => walk(child, depth + 1));
    };
    (byParent.__root__ || []).sort(sortName).forEach((c) => walk(c, 0));
    // Any company whose parent is missing from the list still needs to be selectable
    active.forEach((c) => { if (!out.find((o) => o.company_id === c.company_id)) out.push({ ...c, depth: 0 }); });
    return out;
  }, [companies]);

  const selectedCompany = customerOptions.find((c) => c.company_id === form.company_id);

  // Cases of the selected company + its sub-accounts
  const caseScope = useMemo(() => {
    if (!form.company_id || oneOff) return null;
    const scope = new Set([form.company_id]);
    let added = true;
    while (added) {
      added = false;
      companies.forEach((c) => {
        if (c.parent_company_id && scope.has(c.parent_company_id) && !scope.has(c.company_id)) {
          scope.add(c.company_id);
          added = true;
        }
      });
    }
    return scope;
  }, [companies, form.company_id, oneOff]);

  const pickCompany = (companyId) => {
    const company = customerOptions.find((c) => c.company_id === companyId);
    setForm((f) => ({
      ...f,
      company_id: companyId,
      case_id: 'none',
      client_vat_number: companyId === ONE_OFF ? '' : (company?.vat_number || ''),
    }));
  };

  const totals = useMemo(() => {
    // Prices entered are VAT-INCLUSIVE — VAT is backed out of the total.
    const total = form.line_items.reduce(
      (acc, li) => acc + (Number(li.quantity) || 0) * (Number(li.unit_price) || 0), 0);
    const rate = (Number(form.vat_rate) || 0) / 100;
    const subtotal = rate ? total / (1 + rate) : total;
    return { subtotal, vat: total - subtotal, total };
  }, [form.line_items, form.vat_rate]);

  const setLine = (idx, patch) => setForm((f) => ({
    ...f, line_items: f.line_items.map((li, i) => (i === idx ? { ...li, ...patch } : li)),
  }));

  const submit = async (sendNow) => {
    if (!form.company_id) { toast.error('Select a client company or choose "One-off customer"'); return; }
    if (oneOff && !form.client.name.trim()) { toast.error("Enter the customer's name"); return; }
    const items = form.line_items
      .filter((li) => (li.description || '').trim())
      .map((li) => ({
        description: li.description.trim(),
        quantity: Number(li.quantity) || 0,
        unit_price: Number(li.unit_price) || 0,
      }));
    if (!items.length) { toast.error('Add at least one line item'); return; }

    const payload = {
      company_id: oneOff ? null : form.company_id,
      client: oneOff ? {
        name: form.client.name.trim(),
        email: form.client.email.trim() || null,
        phone: form.client.phone.trim() || null,
        address: form.client.address.trim() || null,
        vat_number: (form.client.vat_number || '').trim() || null,
      } : null,
      client_vat_number: (form.client_vat_number || '').trim() || null,
      case_id: form.case_id !== 'none' ? form.case_id : null,
      line_items: items,
      vat_rate: (Number(form.vat_rate) || 0) / 100,
      issue_date: form.issue_date,
      notes: form.notes || null,
      reference: form.reference || null,
      send_now: sendNow,
      ...(isQuote ? { valid_until: form.end_date } : { due_date: form.end_date, quotation_id: form.quotation_id || null }),
    };

    setBusy(true);
    try {
      await api.post(isQuote ? '/quotations/generate' : '/invoices/generate', payload);
      toast.success(sendNow
        ? `${isQuote ? 'Quotation' : 'Invoice'} created and emailed to the customer`
        : `Draft ${isQuote ? 'quotation' : 'invoice'} created`);
      onOpenChange(false);
      onCreated?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[95vw] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New {isQuote ? 'Quotation' : 'Invoice'}</DialogTitle>
          <DialogDescription>
            Prices entered are VAT-inclusive — VAT and the excl-VAT subtotal are calculated for you.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-semibold">Customer *</Label>
              <Select value={form.company_id} onValueChange={pickCompany}>
                <SelectTrigger className="rounded-none mt-1" data-testid={`${kind}-company-select`}>
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ONE_OFF}>One-off customer (not on the system)</SelectItem>
                  {customerOptions.map((c) => (
                    <SelectItem key={c.company_id} value={c.company_id}>
                      {c.depth > 0 ? `${'\u00a0'.repeat(c.depth * 4)}\u21b3 ${c.company_name} (sub-account)` : c.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedCompany?.parent_company_id && (
                <p className="text-xs text-gray-500 mt-1" data-testid="sub-account-hint">
                  Sub-account of {selectedCompany.parent_company_name || 'parent account'} — billed in its own name
                </p>
              )}
            </div>
            <div>
              <Label className="text-sm font-semibold">Link to Case</Label>
              <Select value={form.case_id} onValueChange={(v) => setForm({ ...form, case_id: v })}>
                <SelectTrigger className="rounded-none mt-1" data-testid={`${kind}-case-select`}>
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {cases
                    .filter((c) => (caseScope ? caseScope.has(c.company_id) : true))
                    .map((c) => (
                      <SelectItem key={c.case_id} value={c.case_id}>
                        {c.service_type} — {c.case_id.slice(-6)}
                        {caseScope && caseScope.size > 1 && c.company_id !== form.company_id ? ` (${c.company_name || 'sub-account'})` : ''}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {oneOff && (
            <div className="border border-dashed border-[#4CAF50]/50 bg-[#4CAF50]/5 p-4 grid sm:grid-cols-2 gap-4" data-testid="one-off-customer-fields">
              <div>
                <Label className="text-sm font-semibold">Customer name *</Label>
                <Input
                  value={form.client.name}
                  onChange={(e) => setForm({ ...form, client: { ...form.client, name: e.target.value } })}
                  placeholder="e.g. Kgomotso Trading CC"
                  className="rounded-none mt-1"
                  data-testid="one-off-name"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold">Email (for sending)</Label>
                <Input
                  type="email"
                  value={form.client.email}
                  onChange={(e) => setForm({ ...form, client: { ...form.client, email: e.target.value } })}
                  className="rounded-none mt-1"
                  data-testid="one-off-email"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold">Phone</Label>
                <Input
                  value={form.client.phone}
                  onChange={(e) => setForm({ ...form, client: { ...form.client, phone: e.target.value } })}
                  className="rounded-none mt-1"
                  data-testid="one-off-phone"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold">VAT number</Label>
                <Input
                  value={form.client.vat_number}
                  onChange={(e) => setForm({ ...form, client: { ...form.client, vat_number: e.target.value }, client_vat_number: e.target.value })}
                  placeholder="e.g. 4123456789"
                  className="rounded-none mt-1 font-mono"
                  data-testid="one-off-vat"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold">Address</Label>
                <Textarea
                  rows={2}
                  value={form.client.address}
                  onChange={(e) => setForm({ ...form, client: { ...form.client, address: e.target.value } })}
                  className="rounded-none mt-1"
                  data-testid="one-off-address"
                />
              </div>
            </div>
          )}

          <div className="border border-gray-200">
            <div className="grid grid-cols-12 gap-2 bg-[#0A192F] text-white text-xs uppercase tracking-wide px-3 py-2">
              <span className="col-span-6">Description</span>
              <span className="col-span-2 text-center">Qty</span>
              <span className="col-span-2 text-right">Unit Price (incl VAT)</span>
              <span className="col-span-2 text-right">Amount</span>
            </div>
            <div className="divide-y divide-gray-100">
              {form.line_items.map((li, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center px-3 py-2">
                  <Input
                    value={li.description}
                    onChange={(e) => setLine(idx, { description: e.target.value })}
                    placeholder="e.g. Tariff review and municipal submission"
                    className="col-span-6 rounded-none"
                    data-testid={`line-description-${idx}`}
                  />
                  <Input
                    type="number" min="0" step="any"
                    value={li.quantity}
                    onChange={(e) => setLine(idx, { quantity: e.target.value })}
                    className="col-span-2 rounded-none text-center"
                    data-testid={`line-quantity-${idx}`}
                  />
                  <Input
                    type="number" min="0" step="any"
                    value={li.unit_price}
                    onChange={(e) => setLine(idx, { unit_price: e.target.value })}
                    placeholder="0.00"
                    className="col-span-2 rounded-none text-right"
                    data-testid={`line-price-${idx}`}
                  />
                  <div className="col-span-2 flex items-center justify-end gap-1">
                    <span className="text-sm text-[#0A192F]">
                      {money((Number(li.quantity) || 0) * (Number(li.unit_price) || 0))}
                    </span>
                    {form.line_items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) })}
                        className="text-gray-400 hover:text-red-600"
                        data-testid={`remove-line-${idx}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="px-3 py-2 border-t border-gray-200">
              <Button
                type="button" variant="outline" size="sm" className="rounded-none"
                onClick={() => setForm({ ...form, line_items: [...form.line_items, emptyLine()] })}
                data-testid="add-line-item-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add line
              </Button>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold">{isQuote ? 'Quotation Date' : 'Issue Date'}</Label>
                  <Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className="rounded-none mt-1" data-testid={`${kind}-issue-date`} />
                </div>
                <div>
                  <Label className="text-sm font-semibold">{isQuote ? 'Valid Until' : 'Due Date'}</Label>
                  <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-none mt-1" data-testid={`${kind}-end-date`} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold">VAT %</Label>
                  <Input type="number" min="0" step="any" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} className="rounded-none mt-1" data-testid={`${kind}-vat-rate`} />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Customer VAT number</Label>
                  <Input
                    value={form.client_vat_number}
                    onChange={(e) => setForm({ ...form, client_vat_number: e.target.value })}
                    placeholder="e.g. 4123456789"
                    className="rounded-none mt-1 font-mono"
                    data-testid={`${kind}-client-vat`}
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold">Reference</Label>
                <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="PO / order no." className="rounded-none mt-1" data-testid={`${kind}-reference`} />
              </div>
              <div>
                <Label className="text-sm font-semibold">Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="rounded-none mt-1" data-testid={`${kind}-notes`} />
              </div>
            </div>

            <div className="bg-[#F8F9FA] border border-gray-200 p-4 space-y-2 self-start" data-testid={`${kind}-totals-preview`}>
              <div className="flex justify-between text-sm"><span className="text-gray-600">Subtotal (excl. VAT)</span><span>{money(totals.subtotal)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-gray-600">VAT @ {Number(form.vat_rate) || 0}% (included)</span><span>{money(totals.vat)}</span></div>
              <div className="flex justify-between pt-2 border-t border-gray-200 font-bold text-[#0A192F]">
                <span>Total (incl. VAT)</span><span data-testid={`${kind}-total-preview`}>{money(totals.total)}</span>
              </div>
              <p className="text-xs text-gray-500 pt-1">The amount you enter already includes VAT — the customer pays the total above.</p>
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
            <Button variant="outline" onClick={() => submit(false)} disabled={busy} className="rounded-none" data-testid={`save-draft-${kind}-btn`}>
              Save as Draft
            </Button>
            <Button onClick={() => submit(true)} disabled={busy} className="bg-[#4CAF50] hover:bg-[#45a049] text-white rounded-none" data-testid={`create-send-${kind}-btn`}>
              <PaperPlaneTilt className="w-4 h-4 mr-2" /> Create & Email
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentBuilderDialog;
